import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";

const ROOMS = [
    {
        url: "https://images.pexels.com/photos/8429844/pexels-photo-8429844.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        title: "Teal Dining",
        tag: "DINING · TEAL",
        accent: "#00A8B5",
        city: "Boca Raton",
        slug: "kids-room-jupiter",
    },
    {
        url: "https://images.pexels.com/photos/37153439/pexels-photo-37153439.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        title: "Midnight Navy Dining",
        tag: "DINING · NAVY",
        accent: "#1E3A8A",
        city: "Palm Beach",
        slug: "office-repaint-palmbeach",
    },
    {
        url: "https://images.pexels.com/photos/17125483/pexels-photo-17125483.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        title: "Rose Bedroom",
        tag: "BEDROOM · PINK",
        accent: "#FF6EA5",
        city: "Delray Beach",
        slug: "kids-room-jupiter",
    },
    {
        url: "https://images.pexels.com/photos/34993915/pexels-photo-34993915.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        title: "Sage Kitchen",
        tag: "KITCHEN · GREEN",
        accent: "#00B26B",
        city: "Wellington",
        slug: "cabinets-boca",
    },
    {
        url: "https://images.pexels.com/photos/6899394/pexels-photo-6899394.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        title: "Firehouse Office",
        tag: "OFFICE · RED",
        accent: "#E11D48",
        city: "Ft. Lauderdale",
        slug: "office-repaint-palmbeach",
    },
    {
        url: "https://images.pexels.com/photos/32383875/pexels-photo-32383875.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        title: "Sunset Living Room",
        tag: "LIVING · ORANGE",
        accent: "#FF6A00",
        city: "Miami",
        slug: "wallpaper-removal-boynton",
    },
    {
        url: "https://images.pexels.com/photos/7166648/pexels-photo-7166648.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        title: "Deep Cobalt Lounge",
        tag: "LIVING · BLUE",
        accent: "#2563EB",
        city: "Jupiter",
        slug: "exterior-delray",
    },
    {
        url: "https://images.pexels.com/photos/10124745/pexels-photo-10124745.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        title: "Buttercup Reading Nook",
        tag: "LIVING · YELLOW",
        accent: "#FACC15",
        city: "Coral Springs",
        slug: "pressure-wash-wellington",
    },
];

/**
 * Recent Work — a gallery of boldly painted interior rooms.
 * Bento-tetris grid with hover reveals and accent-color badges per room.
 */
export default function Fleet() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const drift = useTransform(scrollYProgress, [0, 1], ["-4%", "6%"]);

    return (
        <section
            id="fleet"
            ref={ref}
            data-testid="fleet"
            className="relative bg-black py-24 md:py-40 overflow-hidden"
        >
            <div className="px-6 md:px-16">
                {/* Header */}
                <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-8 mb-14 md:mb-20">
                    <div>
                        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50 mb-6">
                            [ Ch. 01 ] — Recent Work
                        </div>
                        <h2 className="font-display uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.88] tracking-tight max-w-4xl">
                            Walls we've
                            <br />
                            <span className="rainbow-text">crowned lately.</span>
                        </h2>
                    </div>
                    <div className="max-w-md md:text-right">
                        <p className="font-mono text-sm text-white/60">
                            Real South Florida homes, real bold colors. Every
                            room here is the "before → after" your neighbor is
                            going to stare at for a week.
                        </p>
                        <p className="mt-4 font-display uppercase text-xl md:text-2xl leading-tight tracking-tight rainbow-text">
                            Fanatically Detailed.
                            <br />
                            With a Passion for Preparation.
                        </p>
                    </div>
                </div>

                {/* Bento gallery */}
                <div className="grid grid-cols-12 gap-4 md:gap-6">
                    {/* Big feature */}
                    <RoomTile
                        room={ROOMS[0]}
                        index={0}
                        y={drift}
                        big
                        className="col-span-12 md:col-span-8 aspect-[16/10]"
                    />
                    <RoomTile
                        room={ROOMS[1]}
                        index={1}
                        className="col-span-12 md:col-span-4 aspect-[4/5] md:aspect-auto"
                    />

                    {/* Row 2 — 3 mediums */}
                    <RoomTile
                        room={ROOMS[2]}
                        index={2}
                        className="col-span-12 md:col-span-4 aspect-[4/3]"
                    />
                    <RoomTile
                        room={ROOMS[3]}
                        index={3}
                        className="col-span-12 md:col-span-4 aspect-[4/3]"
                    />
                    <RoomTile
                        room={ROOMS[4]}
                        index={4}
                        className="col-span-12 md:col-span-4 aspect-[4/3]"
                    />

                    {/* Row 3 — wide + medium */}
                    <RoomTile
                        room={ROOMS[5]}
                        index={5}
                        y={drift}
                        big
                        className="col-span-12 md:col-span-7 aspect-[16/9]"
                    />
                    <RoomTile
                        room={ROOMS[6]}
                        index={6}
                        className="col-span-12 md:col-span-5 aspect-[16/9] md:aspect-auto"
                    />

                    {/* Full-width final */}
                    <RoomTile
                        room={ROOMS[7]}
                        index={7}
                        className="col-span-12 aspect-[21/6]"
                    />
                </div>

                {/* CTA strip */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
                    className="mt-10 md:mt-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 glass border border-white/15 p-6 md:p-10 bg-black"
                >
                    <div>
                        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">
                            Ready to be next?
                        </div>
                        <div className="font-display uppercase text-3xl md:text-5xl leading-[0.9] tracking-tight mt-2">
                            Pick a color. Pick a room.
                            <br />
                            <span className="rainbow-text">
                                We handle the rest.
                            </span>
                        </div>
                    </div>
                    <a
                        href="tel:5616984044"
                        data-testid="fleet-call-cta"
                        className="group glass-btn relative inline-flex items-center gap-4 border border-white px-7 py-5 font-mono text-xs md:text-sm tracking-[0.24em] uppercase overflow-hidden whitespace-nowrap"
                    >
                        <span className="absolute inset-0 rainbow-bg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        <span className="relative z-10 flex items-center gap-3 group-hover:text-black">
                            <span className="w-2 h-2 rounded-full bg-[#00FF85] blink" />
                            Call 561·698·4044
                            <span className="text-lg leading-none group-hover:translate-x-2 transition-transform duration-500">
                                ↗
                            </span>
                        </span>
                    </a>
                </motion.div>
            </div>
        </section>
    );
}

function RoomTile({ room, index, className = "", y, big = false }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{
                duration: 0.9,
                delay: index * 0.06,
                ease: [0.19, 1, 0.22, 1],
            }}
            style={{ y }}
            data-testid={`room-${index}`}
            className={`relative border border-white/15 overflow-hidden group bg-black cursor-crosshair ${className}`}
        >
            <Link
                to={`/work/${room.slug}`}
                data-testid={`room-link-${index}`}
                className="absolute inset-0 z-20"
                aria-label={`See the ${room.title} job`}
            />
            {/* Image */}
            <img
                src={room.url}
                alt={`${room.title} — painted by PaintKing in ${room.city}`}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-[1.06]"
            />

            {/* Dark scrim */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-black/40 pointer-events-none group-hover:from-black/70 group-hover:to-black/20 transition-[background] duration-700" />

            {/* Accent color splash overlay on hover */}
            <div
                className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-30 transition-opacity duration-700 mix-blend-color"
                style={{ background: room.accent }}
            />

            {/* Top-left tag */}
            <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
                <span
                    className="w-2.5 h-2.5 rounded-full border border-white/70"
                    style={{ background: room.accent }}
                />
                <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/85 backdrop-blur-sm bg-black/40 border border-white/25 px-2 py-1">
                    {String(index + 1).padStart(2, "0")} · {room.tag}
                </span>
            </div>

            {/* Top-right accent chip */}
            <div className="absolute top-4 right-4 z-10 font-mono text-[10px] tracking-[0.24em] uppercase text-white/60">
                {room.accent.toUpperCase()}
            </div>

            {/* Bottom title */}
            <div className="absolute inset-x-0 bottom-0 p-5 md:p-7 z-10">
                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/60">
                    {room.city} · SoFla
                </div>
                <div
                    className={`font-display uppercase leading-[0.9] tracking-tight mt-2 ${
                        big
                            ? "text-3xl md:text-5xl"
                            : "text-2xl md:text-3xl"
                    }`}
                >
                    {room.title}
                </div>
                <div className="mt-3 font-mono text-[10px] tracking-[0.3em] uppercase text-[#FFE600] opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-500">
                    See the job →
                </div>
            </div>

            {/* Corner ticks */}
            <div className="absolute top-3 right-3 w-4 h-4 border-t border-r border-white/30 pointer-events-none" />
            <div className="absolute bottom-3 left-3 w-4 h-4 border-b border-l border-white/30 pointer-events-none" />
        </motion.div>
    );
}
